import { NervaUserData } from 'hydra-data-types';
import { ArrayUtils, isNonEmptyString, replaceSlashWithConjunction } from 'ts-utils';
import { IbsClassification } from '../../../businessLogic/AssessmentQuestionTypes';

/** Answers that say nothing about the person, so they're never shown back to them. */
const NON_ANSWERS = new Set(['More/Other', 'Other', 'I’m not sure', "I'm not sure", 'None']);
/** Stored values whose wording differs from the label the person picked. */
const ANSWER_LABELS: Record<string, string> = { Wind: 'Gas/Wind' };
const MAX_SYMPTOMS = 3;
const MAX_TRIGGERS = 2;
const MAX_ROW_ITEMS = 3;

/** Diagnosis answers shortened to the names people know them by. */
const DIAGNOSIS_LABELS: Record<string, string> = {
  'Irritable Bowel Syndrome (IBS)': 'IBS',
  'Inflammatory Bowel Disease (IBD)': 'IBD',
  'Small Intestinal Bacterial Overgrowth (SIBO)': 'SIBO',
  'Gastro-oesophageal reflux disease (GERD)': 'GERD',
  'Gastrointestinal Reflux Disease (GERD)': 'GERD',
};

const SUBTYPE_LABELS: Record<string, string> = {
  [IbsClassification.IBS_C]: 'IBS-C (constipation)',
  [IbsClassification.IBS_D]: 'IBS-D (diarrhea)',
  [IbsClassification.IBS_M]: 'IBS-M (mixed)',
};

const TIME_OF_DAY_LABELS: Record<string, string> = {
  'When I first wake up': 'when you wake up',
  'In the morning': 'mornings',
  'At lunchtime': 'lunchtimes',
  'In the afternoon/evening': 'afternoons or evenings',
  'Before I fall asleep': 'before bed',
};

/** The goal answers, phrased as where the person will be after the program. */
const GOAL_OUTCOMES: Record<string, string> = {
  'Eat the foods I love': 'Enjoy the foods you love again, without worrying about your gut.',
  'Enjoy my social life and relationships':
    'Say yes to plans with friends and family, without your gut holding you back.',
  'Sleep better': 'Sleep better, without your gut keeping you up.',
  'Travel with confidence': 'Feel confident travelling again, without your gut holding you back.',
  'Be more present at work': 'Be more present at work, without your gut pulling your focus.',
  'Physically comfortable': 'Feel physically comfortable in your body again.',
  'In control of my life': 'Feel back in control of your life, not your gut.',
  'Calm & worry free': 'Feel calm and free from worrying about your gut.',
  'Confident in myself': 'Feel confident in yourself again.',
  'Healthier in my body': 'Feel healthier in your body.',
};
const DEFAULT_OUTCOME = 'Feel calmer and more in control of your gut, day to day.';

export type ProgramAnswers = {
  symptoms: string[];
  triggers: string[];
  goal: string | undefined;
  /** e.g. "5" from the "5 days" weekly commitment answer */
  daysPerWeek: string | undefined;
  timeOfDay: string | undefined;
  /** Diagnosed conditions, e.g. ["IBS", "GERD"] */
  conditions: string[];
  /** IBS or IBD subtype, e.g. "IBS-D (diarrhea)" */
  subtypes: string[];
  /** Other treatments they're doing or planning, e.g. "Low FODMAP diet" */
  alongside: string[];
};

/** Answers the shared Nerva type doesn't list yet; `lowerGISymptoms` is the referred flow's symptom question. */
export type ProgramUserData = Partial<NervaUserData> & {
  lowerGISymptoms?: string[];
  gutBrainDiagnosis?: string[];
  structuralConditions?: string[];
  ibdSubtype?: string[];
  cotreatments?: string[];
};

/** The assessment answers the payment page shows back to the person. */
export function programAnswers(userData: ProgramUserData): ProgramAnswers {
  const answered = (values: string[] | undefined) =>
    (values ?? [])
      .filter((value) => !NON_ANSWERS.has(value))
      .map((value) => ANSWER_LABELS[value] ?? value);

  const diagnosed = [
    ...answered(userData.gutBrainDiagnosis),
    ...answered(userData.structuralConditions),
    ...answered(userData.otherGIIssues),
  ].map((diagnosis) => DIAGNOSIS_LABELS[diagnosis] ?? diagnosis);
  // A subtype answer stays saved when the person goes back and unticks its condition, so it only
  // counts while that condition is still ticked. The main flow asks the IBS type without an IBS
  // diagnosis question, so there the type stands in for one.
  const asksIbsDiagnosis = userData.gutBrainDiagnosis !== undefined;
  const ibsSubtype =
    !asksIbsDiagnosis || diagnosed.includes('IBS')
      ? SUBTYPE_LABELS[userData.classify?.[0] ?? '']
      : undefined;
  const ibdSubtype = diagnosed.includes('IBD') ? answered(userData.ibdSubtype)[0] : undefined;
  const diagnoses = [...(isNonEmptyString(ibsSubtype) ? ['IBS'] : []), ...diagnosed];

  return {
    symptoms: answered(userData.symptoms ?? userData.lowerGISymptoms),
    triggers: answered(userData.triggers),
    // "More/Other" goals fall through to the six-week goal question instead.
    goal: answered(userData.ifFreeFromIbs)[0] ?? answered(userData.howLikeToFeel)[0],
    daysPerWeek: userData.daysPerWeekNerva?.[0]?.split(' ')[0],
    timeOfDay: TIME_OF_DAY_LABELS[userData.timeOfDayNerva?.[0] ?? ''],
    conditions: [...new Set(diagnoses)],
    subtypes: [ibsSubtype, ibdSubtype].filter(isNonEmptyString),
    alongside: answered(userData.cotreatments),
  };
}

/** e.g. "5 days a week · Mornings", or undefined when neither was answered. */
export function programSchedule(answers: ProgramAnswers): string | undefined {
  const parts = [
    isNonEmptyString(answers.daysPerWeek) ? `${answers.daysPerWeek} days a week` : undefined,
    answers.timeOfDay,
  ].filter(isNonEmptyString);
  return parts.length > 0 ? capitaliseFirst(parts.join(' · ')) : undefined;
}

/** A card row's list, e.g. "IBS, GERD, SIBO"; past three the rest become "and more". */
export function listForRow(values: string[]): string {
  const shown = values.slice(0, MAX_ROW_ITEMS).join(', ');
  return values.length > MAX_ROW_ITEMS ? `${shown} and more` : shown;
}

export type PlanStep = { title: string; desc: string };

/** The person's plan as a four-step story, from where they are to their goal. */
export function planSteps(answers: ProgramAnswers, programWeeks: number): PlanStep[] {
  const symptoms =
    answers.symptoms.length > 0 ? listInSentence(answers.symptoms, MAX_SYMPTOMS) : 'gut symptoms';
  const whereYouAre =
    answers.triggers.length > 0
      ? `${capitaliseFirst(symptoms)}, often set off by ${listInSentence(answers.triggers, MAX_TRIGGERS)}.`
      : `Flare-ups of ${symptoms} that get in the way of your day.`;

  return [
    { title: 'Where you are', desc: whereYouAre },
    {
      title: 'Why it happens',
      desc: 'Signals between your gut and brain get crossed, so everyday triggers set off symptoms.',
    },
    {
      title: 'How your program helps',
      desc: 'Guided gut-brain sessions to calm those signals, with human coaching along the way.',
    },
    {
      title: `After ${programWeeks} weeks of Nerva`,
      desc: GOAL_OUTCOMES[answers.goal ?? ''] ?? DEFAULT_OUTCOME,
    },
  ];
}

/** "Anxiety/Stress" reads as "anxiety or stress" mid-sentence; past `max` the rest become "and more". */
function listInSentence(values: string[], max: number): string {
  const words = values.map((value) => replaceSlashWithConjunction(value.toLowerCase()));
  return words.length > max
    ? `${words.slice(0, max).join(', ')} and more`
    : ArrayUtils.toHumanReadableString(words);
}

function capitaliseFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
